import {
  Button,
  Cascader,
  DatePicker,
  Form,
  Input,
  Select,
  Space,
  Upload,
} from 'antd';
import React, { useEffect } from 'react';

import useService from '@/lib/hooks/useService';
import CommunityDevice from '@/types/database/communityDevice';
import { generateHouseHoldNumberCascader } from '@/utils/generateHouseHoldNumberCascader';

import type { FixFormValues, FixModalContentProps } from '../types';
import { getInitialValues, handleDelete, handleEdit } from './helpers';

const { TextArea } = Input;
const { Item, useForm, useWatch } = Form;

const Edit: React.FC<FixModalContentProps & { type: 'create' | 'edit' }> = ({
  fixTableData,
  onClose,
  type,
}) => {
  const [householdNumbers] = useService<{
    _id: string;
    householdNumber: string;
  }>({
    collectionName: 'residentAccountNumbers',
    query: {
      $select: ['householdNumber'],
    },
  });

  const [categories] = useService({
    collectionName: 'categoryMenu',
    query: { name: '報修區域' },
  });

  const [communityDevices] = useService<CommunityDevice>({
    collectionName: 'communityDevice',
    query: { deviceType: '非消耗品' },
  });

  const houseNumbers = generateHouseHoldNumberCascader(
    householdNumbers.map((item) => item.householdNumber)
  );

  const categoryOptions = categories[0]?.data.map((item: string) => ({
    label: item,
    value: item,
  }));

  const [form] = useForm<FixFormValues>();

  const category = useWatch('category', form);

  useEffect(() => {
    if (fixTableData) {
      const initialValues = getInitialValues(fixTableData);
      form.setFieldsValue(initialValues);
    } else {
      form.resetFields();
    }
  }, [fixTableData, form, type]);

  const deviceOptions = communityDevices.map((item) => ({
    label: item.deviceName,
    value: item._id,
  }));

  return (
    <Form
      form={form}
      labelCol={{ offset: 0, span: 24 }}
      onFinish={async (values) => {
        await handleEdit({ values, type, fixId: fixTableData?._id || null });
        onClose();
      }}
    >
      <Item
        label="戶號"
        name="householdNumber"
        required
        rules={[
          {
            required: true,
            message: '請選擇戶號',
          },
        ]}
      >
        <Cascader options={houseNumbers} />
      </Item>
      <Item
        label="報修區域"
        name={['category', 'name']}
        required
        rules={[
          {
            required: true,
            message: '請選擇報修區域',
          },
        ]}
      >
        <Select options={categoryOptions} />
      </Item>
      {category?.name === '公共設備' && (
        <Item
          label="報修設備"
          name={['category', 'communityDeviceId']}
          required
          rules={[
            {
              required: true,
              message: '請選擇報修設備',
            },
          ]}
        >
          <Select options={deviceOptions} />
        </Item>
      )}
      <Item
        label="標題"
        name="title"
        required
        rules={[
          {
            required: true,
            message: '請輸入標題',
          },
        ]}
      >
        <Input />
      </Item>
      <Item
        label="內容"
        name="content"
        required
        rules={[
          {
            required: true,
            message: '請輸入內容',
          },
        ]}
      >
        <TextArea />
      </Item>
      <Item
        label="希望處理時間"
        name="bookingTime"
        required
        rules={[
          {
            required: true,
            message: '請選擇處理時間',
          },
        ]}
      >
        <DatePicker showTime={{ format: 'HH:mm' }} />
      </Item>
      <Item
        label="圖片"
        name="image"
        valuePropName="fileList"
        getValueFromEvent={(e) => {
          return e?.fileList;
        }}
      >
        <Upload
          beforeUpload={() => false}
          accept="image/*"
          onRemove={() => {
            form.setFieldsValue({ image: [] });
          }}
          maxCount={1}
          multiple={false}
          listType="picture"
        >
          <Button>上傳</Button>
        </Upload>
      </Item>
      <Item>
        <Space>
          <Button type="primary" htmlType="submit">
            儲存
          </Button>
          {fixTableData && (
            <Button
              danger
              type="primary"
              onClick={() => handleDelete(fixTableData._id, onClose)}
            >
              刪除
            </Button>
          )}
        </Space>
      </Item>
    </Form>
  );
};

export default Edit;
